import React, { useState } from 'react';
import {
  Button,
  Collapse,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Table,
} from 'reactstrap';
import moment from 'moment';
import toast from 'react-hot-toast';
import ImagePreviewModal from './ImagePreviewModal';

/* JSON columns come back parsed by the driver, but fall back to parsing a
   string so the page still renders if the column is stored as text. */
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const formatJson = (value) => {
  if (!value) return '';
  // raw_payload is a MySQL JSON column — the driver already returns a parsed
  // object, but tolerate a raw string too just in case.
  if (typeof value !== 'string') {
    return JSON.stringify(value, null, 2);
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (e) {
    return value;
  }
};

const installationBadge = (status) => {
  const value = String(status || '').toLowerCase();
  if (!value) return <span>-</span>;
  const style = value.includes('success')
    ? 'bg-soft-success text-success'
    : 'bg-soft-danger text-danger';
  return <span className={`badge ${style}`}>{status}</span>;
};

const DriverAnalysisDetailsOffcanvas = ({ isOpen, toggle, deliveryData }) => {
  const [showRawData, setShowRawData] = useState(false);
  const [preview, setPreview] = useState(null);
  const items = toArray(deliveryData?.items);
  const lifestyleImages = toArray(deliveryData?.lifestyle_images);
  const requestData = formatJson(deliveryData?.raw_payload);

  /* Proof-of-delivery photos plus the signature, as one browsable set - the
     arrows in the lightbox step through all of them. */
  const podImages = [
    ...lifestyleImages
      .map((image, index) => ({
        url: typeof image === 'string' ? image : image?.url,
        label: `Proof of Delivery ${index + 1}`,
      }))
      .filter((image) => !!image.url),
    ...(deliveryData?.signature_url
      ? [{ url: deliveryData.signature_url, label: 'Customer Signature' }]
      : []),
  ];

  const openPreview = (images, index = 0) => setPreview({ images, index });

  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(requestData)
      .then(() => toast.success('Request data copied'))
      .catch(() => toast.error('Unable to copy'));
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '720px' }}
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom">
        Delivery Details {deliveryData?.so_no ? `- ${deliveryData.so_no}` : ''}
      </OffcanvasHeader>
      <OffcanvasBody>
        {!deliveryData ? null : (
          <>
            <div className="mb-4">
              <h6 className="fw-semibold mb-2">Delivery</h6>
              <div className="table-responsive dt-table">
                <Table className=" table mb-0">
                  <tbody>
                    <tr>
                      <td
                        className="table-bordered align-middle fw-semibold"
                        style={{ width: '35%' }}
                      >
                        Delivery Date
                      </td>
                      <td className="table-bordered align-middle">
                        {deliveryData?.delivery_date
                          ? moment(deliveryData.delivery_date).format(
                              'DD MMM, YYYY',
                            )
                          : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-bordered align-middle fw-semibold">
                        Driver
                      </td>
                      <td className="table-bordered align-middle">
                        {deliveryData?.driver_name || '-'}
                        {deliveryData?.driver_emp_id
                          ? ` (${deliveryData.driver_emp_id})`
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-bordered align-middle fw-semibold">
                        Technician
                      </td>
                      <td className="table-bordered align-middle">
                        {deliveryData?.technician_id || '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-bordered align-middle fw-semibold">
                        Customer
                      </td>
                      <td className="table-bordered align-middle">
                        {deliveryData?.customer_name || '-'}
                        {deliveryData?.customer_contact
                          ? ` - ${deliveryData.customer_contact}`
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-bordered align-middle fw-semibold">
                        Store / Dispatch
                      </td>
                      <td className="table-bordered align-middle">
                        {deliveryData?.store_code || '-'}
                        {deliveryData?.dispatch_location
                          ? ` / ${deliveryData.dispatch_location}`
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-bordered align-middle fw-semibold">
                        Delivery Location
                      </td>
                      <td className="table-bordered align-middle">
                        {deliveryData?.del_latitude &&
                        deliveryData?.del_longitude ? (
                          <a
                            href={`https://maps.google.com/?q=${deliveryData.del_latitude},${deliveryData.del_longitude}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {deliveryData.del_latitude},{' '}
                            {deliveryData.del_longitude}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="mb-4">
              <h6 className="fw-semibold mb-2">Items ({items.length})</h6>
              <div className="table-responsive dt-table">
                <Table className=" table mb-0">
                  <thead>
                    <tr>
                      <th nowrap="nowrap">Item Code</th>
                      <th>Description</th>
                      <th nowrap="nowrap">Ord</th>
                      <th nowrap="nowrap">Del</th>
                      <th nowrap="nowrap">Fix</th>
                      <th nowrap="nowrap">Delivery</th>
                      <th nowrap="nowrap">Installation</th>
                      <th nowrap="nowrap">Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length > 0 ? (
                      items.map((item, index) => (
                        <tr key={`${item?.item_code}-${index}`}>
                          <td
                            className="table-bordered align-middle"
                            nowrap="nowrap"
                          >
                            {item?.item_code || '-'}
                          </td>
                          <td className="table-bordered align-middle fs-11">
                            {item?.item_description || '-'}
                          </td>
                          <td className="table-bordered align-middle text-center">
                            {item?.order_quantity ?? 0}
                          </td>
                          <td className="table-bordered align-middle text-center">
                            {item?.delivery_quantity ?? 0}
                          </td>
                          <td className="table-bordered align-middle text-center">
                            {item?.fixing_quantity ?? 0}
                          </td>
                          <td
                            className="table-bordered align-middle"
                            nowrap="nowrap"
                          >
                            {item?.delivery_status || '-'}
                          </td>
                          <td
                            className="table-bordered align-middle"
                            nowrap="nowrap"
                          >
                            {installationBadge(item?.installation_status)}
                          </td>
                          <td
                            className="table-bordered align-middle text-center"
                            nowrap="nowrap"
                          >
                            {item?.normal_images ? (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                title="View image"
                                onClick={() =>
                                  openPreview([
                                    {
                                      url: item.normal_images,
                                      label: item?.item_code || 'Item image',
                                    },
                                  ])
                                }
                              >
                                <i className="mdi mdi-image-outline font-size-16"></i>
                              </Button>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="table-bordered align-middle" colSpan={8}>
                          No items recorded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="mb-3">
              <h6 className="fw-semibold mb-2">
                Proof of Delivery ({lifestyleImages.length})
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {podImages.map((image, index) => {
                  const isSignature = image.label === 'Customer Signature';
                  return (
                    <img
                      key={`${image.url}-${index}`}
                      src={image.url}
                      alt={image.label}
                      role="button"
                      onClick={() => openPreview(podImages, index)}
                      style={{
                        width: '110px',
                        height: '110px',
                        objectFit: isSignature ? 'contain' : 'cover',
                        cursor: 'pointer',
                      }}
                      className={`rounded border${isSignature ? ' bg-white' : ''}`}
                    />
                  );
                })}
                {podImages.length === 0 && (
                  <span className="fs-12">No images uploaded</span>
                )}
              </div>
            </div>

            <div className="mb-3">
              <Button
                color="link"
                size="sm"
                className="p-0 mb-2"
                onClick={() => setShowRawData((prev) => !prev)}
              >
                <i
                  className={`mdi mdi-chevron-${showRawData ? 'down' : 'right'} align-middle`}
                ></i>{' '}
                {showRawData ? 'Hide' : 'Show'} raw request data
              </Button>
              <Collapse isOpen={showRawData}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <b>Request Data</b>
                  {requestData && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      onClick={handleCopy}
                    >
                      <i className="mdi mdi-content-copy align-middle"></i> Copy
                    </Button>
                  )}
                  <span className="fs-11 text-muted ms-auto">
                    Received{' '}
                    {deliveryData?.created_at
                      ? moment(deliveryData.created_at).format(
                          'DD MMM, YYYY hh:mm A',
                        )
                      : '-'}
                  </span>
                </div>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                  {requestData || 'No request data stored'}
                </pre>
              </Collapse>
            </div>
          </>
        )}
      </OffcanvasBody>

      <ImagePreviewModal preview={preview} onClose={() => setPreview(null)} />
    </Offcanvas>
  );
};

export default DriverAnalysisDetailsOffcanvas;
